/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import type { Product } from "@lemmonhq/types";
import { formatPrice } from "@lemmonhq/orders";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { api } from "@app/data/api";
import { useAuth, useCheckout, useShop } from "../../providers";
import { GiftCardModal } from "../POSComponent/GiftCardModal";
import { isTableCreditProduct, tableCreditCheckout } from "./tableCredit";

export type TableCreditTopUpProps = {
  tableId: number;
  tableName?: string;
  /** Pre-filled amount, e.g. what the table is missing. */
  suggestedAmount?: number;
  /** Products already loaded by the caller; fetched when omitted. */
  products?: Product[];
  onClose: () => void;
  /** Called once the credit sale is paid. */
  onDone?: () => void | Promise<void>;
};

/**
 * "Add credit" from a table: picks the credit product (when there are
 * several), then the amount in the GiftCardModal with the table fixed, and
 * hands off to the normal checkout, so the credit is paid like any sale
 * before the table can spend it.
 */
export const TableCreditTopUp = defineComponent<TableCreditTopUpProps>({
  name: "TableCreditTopUp",
  props: [
    "tableId",
    "tableName",
    "suggestedAmount",
    "products",
    "onClose",
    "onDone",
  ],
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();
    const shop = useShop();
    const checkout = useCheckout();
    const currency = computed(
      () => auth.user.value?.restaurant.currency || "CHF",
    );

    const loaded = ref<Product[] | null>(props.products ?? null);
    const creditProducts = computed(() =>
      (loaded.value || []).filter(isTableCreditProduct),
    );
    const picked = ref<Product | null>(null);
    // With a single credit product there is nothing to choose.
    const product = computed(
      () =>
        picked.value ??
        (creditProducts.value.length === 1 ? creditProducts.value[0] : null),
    );

    onMounted(async () => {
      if (!loaded.value) loaded.value = await api.getProducts();
    });

    const sell = (product: Product, amount: number) => {
      if (!(amount > 0)) return;
      props.onClose();
      checkout.setCheckout(tableCreditCheckout(product, props.tableId, amount));
      checkout.show({
        onCancel() {},
        async onSuccess() {
          shop.refreshTables();
          await props.onDone?.();
        },
      });
    };

    return () =>
      product.value ? (
        <GiftCardModal
          product={product.value}
          defaultTableId={props.tableId}
          lockTable={true}
          suggestedAmount={props.suggestedAmount}
          onClose={() => {
            if (picked.value && creditProducts.value.length > 1) {
              picked.value = null;
            } else {
              props.onClose();
            }
          }}
          onConfirm={({ amount }) => sell(product.value!, amount)}
        />
      ) : (
        <LemmonModal
          title={t("pos.table_credit.add_credit")}
          onClose={props.onClose}
        >
          <div class="flex flex-col gap-3 p-4">
            {props.tableName ? (
              <p class="text-sm text-base-content/70">
                {t("pos.table_credit.add_credit_to").replace(
                  "{0}",
                  props.tableName,
                )}
              </p>
            ) : null}

            {loaded.value && creditProducts.value.length === 0 ? (
              <div class="alert">
                <span class="material-symbols-outlined">info</span>
                <span>{t("pos.table_credit.no_credit_product")}</span>
              </div>
            ) : null}

            {creditProducts.value.map((product) => (
              <button
                key={product.id}
                type="button"
                class="btn btn-lg justify-between"
                onClick={() => (picked.value = product)}
              >
                <span>{product.name}</span>
                {parseFloat(product.price) > 0 ? (
                  <span>
                    {formatPrice(
                      parseFloat(product.price),
                      true,
                      2,
                      currency.value,
                    )}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </LemmonModal>
      );
  },
});
